import React, { useState, useEffect } from "react";
import AnimatedLoader from "../../modules/ui/animatedLoader";
import { showDeliveryControlPanelSettings, notifyFoodArrived } from "../../shared/services/Api/CommonService";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { toast } from "react-toastify";

const WeekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const getCurrentDay = () => {
  const days = new Date().getDay();
  return WeekDays[days - 1];
};

const DeliveryControlPanelCard = ({ user_id }) => {

  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [activeDay, setActiveDay] = useState(params.get("day") ?? getCurrentDay());
  const [restaurantState, setRestaurantState] = useState({});
  const [groupOrders, setGroupOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    history.replace(`${location.pathname}?day=${activeDay}`);
  }, [activeDay]);

  // Fetch group orders
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const response = await showDeliveryControlPanelSettings({
          user_id,
          params: { day: activeDay }
        });
        setGroupOrders(response.group_orders ?? []);

        // Initialize notes for each order if not already set
        const initialState = {};
        (response.group_orders ?? []).forEach(order => {
          initialState[order.id] = {
            status: "waiting",
            sent: 0,
            total: order.members_count,
            success: [],
            failure: [],
            completed: false,
            notes: `🍽 Your lunch order from ${order.chef_name} has arrived, please come pick it up! It's ready for you.`
          };
        });
        setRestaurantState(initialState);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [activeDay, user_id]);

  // notify food arrived
  const handleSendMessages = async (order) => {
    setRestaurantState((prev) => ({
      ...prev,
      [order.id]: {
        ...prev[order.id],
        status: "active",
        sent: 0,
        total: order?.members_count,
        success: [],
        failure: [],
        completed: false,
      },
    }));
    try {
      setIsLoading(true);
      const response = await notifyFoodArrived({
        order_id: order.id,
        notes: restaurantState[order.id]?.notes || "",
      });
      setRestaurantState((prev) => ({
        ...prev,
        [order.id]: {
          ...prev[order.id],
          sent: response?.report?.employees_notified_count,
          success: response?.report?.notified_employees.map(f => f.recipient_name),
          failure: response?.report?.failed_notifications.map(f => f.recipient_name),
          completed: true,
          status: "active",
        },
      }));

      toast.success(response?.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AnimatedLoader isLoading={isLoading} />
      <div className="delivery-control-panel-container">
        <div className="manager-tip">
          <strong>Tip for Office Managers</strong>
          <p>
            Please ensure all joined members have an up-to-date{" "}
            <strong>email address and phone number</strong> so they receive these
            notifications successfully.
          </p>
        </div>

        <div className="week-tabs">
          {WeekDays.map((day) => (
            <button
              key={day}
              className={`day-tab ${activeDay?.toLowerCase() === day.toLowerCase() ? "active" : ""}`}
              onClick={() => setActiveDay(day)}
            >
              {day}
            </button>
          ))}
        </div>

        <section className="day-panel">
          {groupOrders.length > 0 ? (
            groupOrders.map((order) => {
              const isDelivered = order?.status?.toLowerCase() === "delivered";
              const state = restaurantState[order.id] || {};

              const progressPercent = state.total > 0 ? (state.sent / state.total) * 100 : 0;

              return (
                <div key={order.id} className="restaurant-card">
                  <div className="restaurant-header">
                    <div className="restaurant-info">
                      <h3>{order.chef_name}</h3>
                      <p>{order.name}</p>
                      <span className="orders">{order.members_count} orders</span>
                    </div>
                  </div>

                  <div className="message-box">
                    <label>Message to employees</label>
                    <textarea
                      value={state.notes || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setRestaurantState((prev) => ({
                          ...prev,
                          [order.id]: {
                            ...prev[order.id],
                            notes: value,
                          },
                        }));
                      }}
                      className="message-textarea"
                    />
                  </div>

                  <div className="action-row">
                    <button
                      className="arrived-btn"
                      disabled={isDelivered}
                      onClick={() => handleSendMessages(order)}
                    >
                      {isDelivered ? "Marked as Delivered" : "Mark Arrived"}
                    </button>
                    <button
                      className="resend-btn"
                      onClick={() => handleSendMessages(order)}
                    >
                      Resend Message
                    </button>
                  </div>

                  {state.status === "active" && (
                    <div className="progress">
                      <div className="progress-info">
                        <span>
                          {state.sent === state.total && state.total > 0
                            ? "Notification sent"
                            : "Sending notifications…"}
                        </span>
                        <span className="count-text">
                          {state.sent} of {state.total} notified
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {state.completed && (
                    <div className="delivery-summary">
                      {state.success.length > 0 && (
                        <p className="success-text">
                          Message successfully sent to: {state.success.join(", ")}
                        </p>
                      )}
                      {state.failure.length > 0 && (
                        <p className="failure-text">
                          Message failed to send to {state.failure.join(", ")} — please contact directly.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="no-data">
              <p>No data found</p>
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default DeliveryControlPanelCard;
